import React, { useRef } from 'react';
import { styles } from './styles';
import { COLORS } from '../../constants';

const ProfileForm = ({ 
  name, 
  setName,
  bio,
  setBio,
  email,
  currentPhotoURL,
  photoFile, 
  setPhotoFile, 
  onSubmit, 
  saving,
  onCancel 
}) => {
  const fileInputRef = useRef(null);

  const UserIcon = () => (
    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="1.5">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );

  const CameraIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  );

  const previewURL = photoFile ? URL.createObjectURL(photoFile) : currentPhotoURL;

  return (
    <form onSubmit={onSubmit} style={styles.form}>
      {/* Profile Photo Section */}
      <div style={styles.photoSection}>
        <div style={styles.photoContainer}>
          {previewURL ? (
            <img 
              src={previewURL} 
              alt="Profile" 
              style={styles.profilePhoto}
            />
          ) : (
            <div style={styles.photoPlaceholder}>
              <UserIcon />
            </div>
          )}
          <button
            type="button"
            style={styles.changePhotoButton}
            onClick={() => fileInputRef.current?.click()}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <CameraIcon />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
          style={styles.hiddenFileInput}
        />
        <p style={styles.photoHint}>Click the camera icon to change your photo</p>
      </div>

      {/* Form Fields */}
      <div style={styles.fieldsContainer}>
        {/* Email (Read-only) */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            value={email}
            disabled
            style={styles.inputDisabled}
          />
          <p style={styles.fieldHint}>Email cannot be changed</p>
        </div>

        {/* Display Name */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>
            Display Name <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={styles.input}
            placeholder="Enter your display name"
            maxLength={50}
          />
          <p style={styles.fieldHint}>{name.length}/50 characters</p>
        </div>

        {/* Bio */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Bio</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            style={styles.textarea}
            placeholder="Tell others about yourself..."
            maxLength={200}
            rows={4}
          />
          <p style={styles.fieldHint}>{bio.length}/200 characters</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={styles.actionsContainer}>
        <button 
          type="button" 
          style={styles.cancelButton} 
          onClick={onCancel}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = COLORS.lightGray;
            e.currentTarget.style.borderColor = COLORS.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = COLORS.mediumGray;
          }}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          style={saving ? styles.saveButtonDisabled : styles.saveButton}
          disabled={saving}
          onMouseEnter={(e) => {
            if (!saving) {
              e.currentTarget.style.background = '#b01121';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.primary}40`;
            }
          }}
          onMouseLeave={(e) => {
            if (!saving) {
              e.currentTarget.style.background = COLORS.primary;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary}30`;
            }
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;